import { gql, QueryResult } from '@apollo/client'
import * as React from 'react'
import { useCallback, useState } from 'react'
import styled from 'styled-components'
import Button from '../../../../common/src/components/Button'
import { Link } from '../../../../common/src/components/Link'
import { Spacer, BodyTextSpan, H2, BodyText, SmallerMonoTextSpan } from '../../../../common/src/components/core'
import { JobName, FetchFirmDetailsQuery, FetchFirmDetailsQueryVariables } from '../../../../common/src/types.gen'
import { Fragments } from '../../graphql/fragments'
import { runAdminTaskUncaught, updateFirm } from '../../graphql/mutations'
import EditHeader from '../components/EditHeader'
import JobsHistorySection from '../components/JobsHistorySection'
import { PollingQuery } from '../components/PollingQuery'
import { getTaskPath } from '../route'
import FeatureFlagsSection from '../tasks/FeatureFlagsSection'
import FirmEditBoxes from './FirmEditBoxes'
import { AdminJobSummary, JobsRunningSection } from './JobsRunningSection'

/**
 * WARNING
 *
 * if you edit this query, you will also have to edit the place that mutates the firm.
 * see `updateFirm` in `mutations.ts` for example.
 */
const fetchFirmDetails = gql`
  query FetchFirmDetails($firmId: Int!) {
    firm(firmId: $firmId) {
      ...Firm
      config
      firmTask {
        id
      }
      exchangeCredsWithObfuscatedSecrets {
        ...ExchangeCreds
      }
      msGraphCredsWithObfuscatedSecrets {
        ...MsGraphCreds
      }
      gSuiteCredsWithObfuscatedSecrets {
        ...GSuiteCreds
      }
      resmanInfoWithObfuscatedSecrets {
        ...BasicPmInfo
      }
      apmInfoWithObfuscatedSecrets {
        ...ApmInfo
      }
      entrataInfoWithObfuscatedSecrets {
        ...EntrataInfo
      }
      yardiInfoWithObfuscatedSecrets {
        ...YardiInfo
      }
    }
  }
  ${Fragments.firm}
  ${Fragments.exchangeCreds}
  ${Fragments.msGraphCreds}
  ${Fragments.gSuiteCreds}
  ${Fragments.resmanInfo}
  ${Fragments.apmInfo}
  ${Fragments.entrataInfo}
  ${Fragments.yardiInfo}
`

interface FirmDetailProps {
  firmId: number
  canViewFirmSecrets: boolean
}

function getFirmJobs(firmId: number): AdminJobSummary[] {
  const ret = [
    {
      text: 'Sync Now',
      description:
        'Performs a PM sync on the entire firm. Each property belonging to the firm will be updated with the most recent data from whatever PM this firm is using.',
      args: {
        name: JobName.ADMIN_RERUN_AUDITS,
        firmId,
      },
    },
    {
      text: 'Gsuite OAuth',
      description: 'Sometimes our authentication becomes stale. Use this job to generate fresh credentials.',
      args: {
        name: JobName.ADMIN_GSUITE_OAUTH,
        firmId,
      },
      argumentDescription:
        'Follow the instructions on generating Gsuite OAuth credentials above. One of the steps requires you to paste the generated credentials from the google admin console into the input below, and run this job.',
      argumentRequired: true,
    },
    {
      text: 'Microsoft OAuth',
      description: 'Sometimes our authentication becomes stale. Use this job to generate fresh credentials.',
      args: {
        name: JobName.ADMIN_MICROSOFT_OAUTH,
        firmId,
      },
      argumentDescription:
        'Follow the instructions on generating Microsoft OAuth credentials above. One of the steps requires you to paste the generated credentials from the Azure page into the input below, and run this job.',
      argumentRequired: true,
    },
    {
      text: 'Fetch Calendars',
      description: 'Fetches the freshest copy of the calendars of each agent that belongs to this firm.',
      args: {
        name: JobName.ADMIN_FETCH_CALENDARS,
        firmId,
      },
    },
    {
      text: 'Repost Units And Campaigns',
      description: 'AppFolio only. Repost all posted listables (i.e. units or campagins) to ILS via AppFolio.',
      args: {
        name: JobName.ADMIN_REPOST_ALL_LISTABLES,
        firmId,
      },
    },
    {
      text: 'Backfill Guest Cards',
      description:
        'Retries *all* failed guest cards for a firm in the last 90 days, regardless of count. Produces a summary of success or failure for each.',
      args: {
        name: JobName.ADMIN_BACKFILL_GUEST_CARDS,
        firmId,
      },
    },
    {
      text: 'Create Reports',
      description:
        'Runs the ReportService tick for this firm and saves all the specified reports for all the agents and bosses.',
      args: {
        name: JobName.ADMIN_CREATE_REPORTS,
        firmId,
      },
      argumentDescription:
        'A comma-separated list of report names to run. These could be the names of CSV reports or Email reports.',
      argumentRequired: true,
    },
    {
      text: 'Twilio Migration: List Phone Numbers',
      description:
        "**Step 1** of the Lisa to Leasing Performer phone migration. Builds the Twilio ticket text: the account SIDs and the vhost's active phone numbers. Changes nothing. Date: `2026-09-15`. Time: `10:00 AM PST`, with a US zone (PST, PDT, MST, MDT, CST, CDT, EST, EDT, HST). No commas. At least 4 hours ahead. Use **Copy result** on the result below. To run one property, add its Lisa property task id as a fourth field. The id is on the property task's page. Pilot: for each pilot property, run Step 1 then Step 3 with its task id. Finish all pilot Step 3 runs before you run Step 1 without a task id, because Step 1 lists only unreleased numbers. Then run both steps without a task id.",
      args: {
        name: JobName.ADMIN_TWILIO_MIGRATION_LIST_NUMBERS,
        firmId,
      },
      argumentDescription:
        'vhost,migration_date,migration_time, e.g. acme.qa,2026-09-15,10:00 AM PST. For one property add the property task id: acme.qa,2026-09-15,10:00 AM PST,45678',
      argumentRequired: true,
    },
  ]

  if (window.location.href.includes('localhost')) {
    ret.push({
      text: 'Clear PM Data',
      description: 'Development mode ONLY. deletes all the PM data of this firm from our database',
      args: {
        name: JobName.ADMIN_CLEAR_PM_DATA,
        firmId,
      },
    })
  }

  return ret
}

class FirmDetailImpl extends React.PureComponent<
  FirmDetailProps & QueryResult<FetchFirmDetailsQuery, FetchFirmDetailsQueryVariables>
> {
  render() {
    if (this.props.data == null) {
      return null
    }
    const { canViewFirmSecrets } = this.props
    const { firm } = this.props.data
    if (firm == null) {
      return null
    }

    return (
      <div>
        <EditHeader header={firm.firmName} onEdit={this.onEditBound} />
        <LinkToTask firm={firm} />
        <Spacer height={8} />
        <FirmEditBoxes firmInfo={firm} onEdit={this.onUpdateFirmBound} canViewFirmSecrets={canViewFirmSecrets} />
        <Spacer height={64} />
        <DataLinksSection firm={firm} />
        <Spacer height={64} />
        <FeatureFlagsSection firm={firm} />
        <Spacer height={64} />
        <FirmJobsSection firm={firm} />
        <Spacer height={96} />
        <Spacer height={96} />
      </div>
    )
  }

  private onEditBound = (firmName: string) => this.onUpdateFirmBound('firmName', firmName)

  private onUpdateFirmBound = (key: string, value: any): Promise<any> => {
    const { firm } = this.props.data!
    if (firm == null) {
      return Promise.resolve(null)
    }
    return updateFirm(firm, key, value, true)
  }
}

function LinkToTask({ firm }: { firm: FetchFirmDetailsQuery['firm'] }) {
  if (!firm.firmTask) {
    return null
  }

  return (
    <Link to={getTaskPath(firm.firmTask.id)}>
      <BodyTextSpan color="snow">firm task</BodyTextSpan>
    </Link>
  )
}

function FirmJobsSection({ firm }: { firm: FetchFirmDetailsQuery['firm'] }) {
  return (
    <>
      <H2>Jobs</H2>
      <JobsRunningSection jobs={getFirmJobs(firm.id)} />
      {firm.pmKeyJsons.map(pmKeyJson => (
        <div key={pmKeyJson}>
          <Spacer height={16} />
          <JobsHistorySection jobKey={pmKeyJson} />
        </div>
      ))}
    </>
  )
}

/**
 * Rotates the firm's CSV API key (hard cutover: old key dies immediately, api-docs email with fresh
 * URLs auto-sends to the firm's bosses/agents). The page polls, so the new key and "Last rotated"
 * timestamp show up on the next refetch.
 */
export function RotateApiKeySection({ firm }: Readonly<{ firm: FetchFirmDetailsQuery['firm'] }>) {
  const [state, setState] = useState<'idle' | 'running' | 'success' | 'error'>('idle')
  const apiKeyRotatedAt = firm.config == null ? null : (JSON.parse(firm.config).apiKeyRotatedAt as string | null)

  const onRotate = useCallback(() => {
    const confirmed = window.confirm(
      `Rotate the CSV API key for ${firm.firmName}?\n\n` +
        'The old key stops working immediately (no grace window) — any Excel data connection using it breaks ' +
        "until the customer re-pastes a URL from the api-docs email, which is sent automatically to the firm's " +
        'bosses and agents with the new key.'
    )
    if (!confirmed) {
      return
    }
    setState('running')
    runAdminTaskUncaught({ name: JobName.ADMIN_ROTATE_FIRM_API_KEY, firmId: firm.id })
      .then(() => setState('success'))
      .catch(() => setState('error'))
  }, [firm.id, firm.firmName])

  return (
    <BodyTextSpan>
      <Button size="small" onClick={onRotate} disabled={state === 'running'}>
        Rotate API Key
      </Button>
      <Spacer width={16} />
      <BodyTextSpan color="silver">
        Last rotated: {apiKeyRotatedAt ? new Date(apiKeyRotatedAt).toLocaleString() : 'never'}
      </BodyTextSpan>
      {state === 'success' && (
        <>
          <Spacer width={16} />
          <BodyTextSpan color="mint">rotation job submitted — key updates on next page refresh</BodyTextSpan>
        </>
      )}
      {state === 'error' && (
        <>
          <Spacer width={16} />
          <BodyTextSpan color="coral">error submitting rotation job</BodyTextSpan>
        </>
      )}
    </BodyTextSpan>
  )
}

function DataLinksSection({ firm }: { firm: FetchFirmDetailsQuery['firm'] }) {
  const [format, setFormat] = useState<string>('csv')

  if (firm.config == null) {
    return null
  }
  const apiKey = JSON.parse(firm.config).apiKey as string
  if (apiKey == null) {
    return null
  }
  const formats = ['csv', 'tsv', 'inline']
  return (
    <>
      <H2>
        Data
        <Spacer width={8} />
        <BodyTextSpan>
          format{' '}
          {formats.map(f => (
            <span key={f}>
              <Spacer width={8} />
              <Button size="small" onClick={useCallback(() => setFormat(f), [f])} disabled={f === format}>
                {f}
              </Button>
            </span>
          ))}
        </BodyTextSpan>
      </H2>
      <Spacer height={16} />
      <RotateApiKeySection firm={firm} />
      <Spacer height={16} />
      <BodyText color="silver">
        <p>CSV API, available to clients:</p>
        <BulletList>
          <li>
            <DataLabel>prospects:</DataLabel>
            <DataLink href={getCsvLink(firm.id, apiKey, 'prospects', format)} elide={apiKey} />
          </li>
          <li>
            <DataLabel>showings:</DataLabel>
            <DataLink href={getCsvLink(firm.id, apiKey, 'showings', format)} elide={apiKey} />
          </li>
          <li>
            <DataLabel>agents:</DataLabel>
            <DataLink href={getCsvLink(firm.id, apiKey, 'agents', format)} elide={apiKey} />
          </li>
          <li>
            <DataLabel>marketing #s:</DataLabel>
            <DataLink href={getCsvLink(firm.id, apiKey, 'marketing_tracking', format)} elide={apiKey} />
          </li>
          <li>
            <DataLabel>settings:</DataLabel>
            <DataLink href={getCsvLink(firm.id, apiKey, 'settings', format)} elide={apiKey} />
          </li>
          <li>
            <DataLabel>timeseries shows:</DataLabel>
            <DataLink href={getCsvLink(firm.id, apiKey, 'timeseries_showings', format)} elide={apiKey} />
          </li>
          <li>
            <DataLabel>timeseries leads:</DataLabel>
            <DataLink href={getCsvLink(firm.id, apiKey, 'timeseries_leads', format)} elide={apiKey} />
          </li>
          <li>
            <DataLabel>feedback:</DataLabel>
            <DataLink href={getCsvLink(firm.id, apiKey, 'feedback', format)} elide={apiKey} />
          </li>
        </BulletList>
        <p></p>
        <Spacer height={16} />
        <p>Internal, admin only:</p>
        <BulletList>
          <li>
            <DataLabel>availability:</DataLabel>
            <DataLink href={`/leasing/data/availability?firm=${firm.id}`} />
            <br />
            <BodyTextSpan>Shows pricing and availability across all floorplans in the firm.</BodyTextSpan>
          </li>
        </BulletList>
      </BodyText>
    </>
  )
}

function getCsvLink(firmId: number, apiKey: string, report: string, format: string) {
  const fmt = format === 'csv' ? '' : `&format=${format}`
  return `/api/csv?firm=${firmId}&key=${apiKey}${fmt}&report=${report}`
}

const DataLabel = styled.div`
  display: inline-block;
  width: 128px;
`

function DataLink({ href, elide }: { href: string; elide?: string }) {
  const text = elide ? href.replace(elide, '…') : href
  return (
    <Link href={href}>
      <SmallerMonoTextSpan>{text}</SmallerMonoTextSpan>
    </Link>
  )
}

const BulletList = styled.ol`
  list-style: circle;

  & li {
    margin: 8px 16px 8px 32px;
  }
`

export default function FirmDetail(props: FirmDetailProps) {
  return (
    <PollingQuery query={fetchFirmDetails} variables={{ firmId: props.firmId }}>
      {(res: QueryResult<FetchFirmDetailsQuery, FetchFirmDetailsQueryVariables>) => (
        <FirmDetailImpl {...props} {...res} />
      )}
    </PollingQuery>
  )
}
